import CT from '@marketplace-ai/tags';
import { FilterWrapperParams, IAfterGuiAttachedParams } from 'ag-grid-community';
import { useGridFilterDisplay, type CustomFilterDisplayProps } from 'ag-grid-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export const CustomSetFilter = ({
  model,
  onModelChange,
  api,
  getValue,
}: CustomFilterDisplayProps & FilterWrapperParams) => {
  const refInput = useRef<HTMLInputElement>(null);
  const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
    if (!params || !params.suppressFocus) {
      refInput.current?.focus();
    }
  }, []);

  useGridFilterDisplay({
    afterGuiAttached,
  });

  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const values = new Set<string>();
    api.forEachNode((node) => {
      const value = getValue(node);
      if (value != null) values.add(String(value));
    });
    setAvailableOptions(Array.from(values).sort());
  }, [api, getValue]);

  const filteredOptions = availableOptions.filter((value) =>
    value.toLowerCase().includes(searchText.toLowerCase())
  );

  const onCheckboxChange = (value: string) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedValues(newSelected);
  };

  const selectAll = () => {
    setSelectedValues(new Set(availableOptions));
  };

  const clearAll = () => {
    setSelectedValues(new Set());
  };

  useEffect(() => {
    const values = Array.from(selectedValues);
    onModelChange(values.length > 0 ? values : null);
  }, [selectedValues, onModelChange]);

  // When the grid clears this filter externally, also clear the search box
  // and any checked values so the UI matches the (empty) filter state.
  useEffect(() => {
    if (model == null) {
      setSelectedValues(new Set());
      setSearchText('');
    }
  }, [model]);

  return (
    <div className="p-3 w-52 overflow-hidden flex flex-col max-h-72">
      <input
        ref={refInput}
        type="text"
        placeholder={'Search...'}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className="mb-2 px-2.5 py-1.5 border border-grey-800 rounded-md text-sm bg-white transition-colors duration-150 focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12 focus:outline-none placeholder:text-grey-600"
      />

      <div className="flex gap-2 mb-2">
        <button
          onClick={selectAll}
          className="text-xs font-medium text-primary-50 hover:text-primary-t-300 transition-colors"
        >
          Select All
        </button>
        <button
          onClick={clearAll}
          className="text-xs font-medium text-primary-50 hover:text-primary-t-300 transition-colors"
        >
          Clear
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="text-xs text-grey-400 p-2">No items found</div>
        ) : (
          filteredOptions.map((value) => (
            <label
              key={value}
              className="flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-grey-900 transition-colors"
            >
              <input
                data-cy={`${CT.agGrid.filterCheckbox}-${value}`}
                type="checkbox"
                checked={selectedValues.has(value)}
                onChange={() => onCheckboxChange(value)}
                className="size-3.5 rounded border-grey-800 text-primary-50 focus:ring-primary-50/20"
              />
              <span className="text-sm text-grey-100 truncate" title={value}>
                {value}
              </span>
            </label>
          ))
        )}
      </div>

      {selectedValues.size > 0 && (
        <div className="text-xs text-grey-400 mt-2 pt-2 border-t border-grey-800">
          {selectedValues.size} of {availableOptions.length} selected
        </div>
      )}
    </div>
  );
};
